// Schemas Zod das respostas cruas do Xtream Codes API.
// Painéis variam (XUI.one, Flussonic, original) — schemas defensivos.
import { z } from 'zod';
// Helper: aceita string OR number, converte pra string
const flexString = z.union([z.string(), z.number()]).transform((v) => String(v));
// Helper: metadado string OPCIONAL que provedores Xtream "sujos" às vezes mandam
// como `null` em vez de omitir. `z.string().optional()` aceita `undefined` mas
// REJEITA `null` — e como a ficha de série usa `.parse()` (hard, não drop por item
// como o listing), UM único campo null derruba a ficha INTEIRA: o backend estoura
// ZodError, o SSE manda `event: error` e o JSON responde 400, então a série NÃO
// carrega no app (sintoma: "abrir a série fecha o app"/tela de erro). Caso real de
// prod: HXTV/softxd manda `info.releasedate`/`info.plot = null` em episódios de
// séries longas (ex.: "Lost" — 6 eps com null). `.nullable()` aceita e PRESERVA o
// null; todo consumidor já lida com ausência (typeof guards no catalog-service,
// `?.` no app TV). Mesma filosofia tolerante do union de `backdrop_path` abaixo.
const nullableString = z.string().nullable().optional();
/**
 * Normalização canônica de flag booleana do Xtream: aceita "1"/1/true/"true"
 * como VERDADEIRO e trata TODO o resto (inclusive `undefined`) como falso.
 *
 * ── POR QUE É FUNÇÃO EXPORTADA E NÃO SÓ O `.transform` DO SCHEMA ──
 * O veredito de credencial do adapter (`inspectXtreamAuthEnvelope` em
 * xtream-adapter.ts) precisa decidir sobre o payload CRU, antes do parse, nos
 * caminhos de catálogo — que não têm um `XtreamAuth` pra consumir. Enquanto essa
 * decisão foi uma comparação artesanal lá (`user_info?.auth === 0`), existiram
 * DOIS parsers divergentes da mesma resposta: o schema normalizava `"0"` pra
 * `false` e o veredito lia o cru, onde `"0" === 0` é falso — e um painel que
 * recusava com a string `"0"` era lido como login BEM-SUCEDIDO.
 *
 * ALLOW-LIST, nunca deny-list: enumerar as grafias de recusa ("0"/false/"false"/
 * ausente/…) é uma lista que nunca fecha. Aqui só as 4 grafias de SUCESSO passam.
 *
 * ⚠️ NÃO é a única normalização de flag do package, e a diferença importa.
 * `normalize.ts` (`normalizeLiveStream`) tem a DELA, escrita à mão para `is_adult`, e
 * ela aceita `'1'`/`1`/`true` mas **não** `'true'` — ou seja, um painel que marque
 * conteúdo adulto com a string `"true"` não é reconhecido. Isso é anterior a esta
 * função e NÃO foi unificado de propósito: alinhar as duas mudaria a classificação de
 * conteúdo adulto (direção segura, mas é decisão de produto, não refactor de carona
 * num fix de auth). Registrado como achado à parte.
 *
 * Quem for conferir, use o grep que enumera as DUAS — o grep pelo nome desta função
 * só encontra a si mesmo, que foi como a afirmação anterior ("a única") passou:
 *   grep -rnE "normalizeXtreamFlag|flexBool|=== '1'|=== true" packages/iptv-client/src --include=*.ts
 */
export function normalizeXtreamFlag(v) {
    // CAIXA e espaço em volta não mudam o significado de "sim" — `"TRUE"`, `"True"` e
    // `" 1"` são o mesmo sim que `"true"` e `1`. Aceitá-los NÃO alarga o vocabulário
    // (continuam sendo duas palavras: `1` e `true`), e fecha o modo de falha caro deste
    // endurecimento: um painel legítimo que responda `"TRUE"` derrubaria o login de toda
    // a base daquele provedor, com o sintoma idêntico ao de senha errada.
    //
    // O que deliberadamente NÃO entra: `"yes"`, `"on"`, `2`, ou qualquer inteiro
    // não-`1`. Aí seria ampliar o vocabulário de aceitação — e este predicado guarda um
    // portão de credencial, onde cada grafia nova é superfície nova.
    if (typeof v === 'boolean')
        return v;
    if (typeof v === 'number')
        return v === 1;
    if (typeof v !== 'string')
        return false;
    const t = v.trim().toLowerCase();
    return t === '1' || t === 'true';
}
/**
 * O painel disse NÃO numa grafia que a gente RECONHECE?
 *
 * Companheira da `normalizeXtreamFlag`, e existe pra uma distinção de observabilidade,
 * não de autorização: as duas respostas são RECUSA de qualquer jeito (allow-list não
 * abre exceção por dúvida). O que muda é o alarme.
 *
 * "Não reconhecido" (`"yes"`, `"on"`, `2`) significa que o painel fala um vocabulário
 * que o nosso veredito não entende — e aí a recusa pode estar derrubando um provedor
 * legítimo, o que precisa de WARN. "Não conhecido" (`0`, `"false"`, `""`) é a senha
 * errada de todo dia, alto volume, e logar isso afogaria justamente o sinal raro.
 *
 * `null` entra como negação conhecida (é o vocabulário comum de "vazio"), enquanto
 * array/objeto ficam de fora: shape estranho merece sinal.
 */
export function ehNegacaoXtreamConhecida(v) {
    if (v === null)
        return true;
    if (typeof v === 'boolean')
        return v === false;
    if (typeof v === 'number')
        return v === 0;
    if (typeof v !== 'string')
        return false;
    const t = v.trim().toLowerCase();
    return t === '0' || t === 'false' || t === '';
}
// Helper: aceita "1"/"0"/1/0/true/false, converte pra boolean
const flexBool = z.union([z.string(), z.number(), z.boolean()]).transform(normalizeXtreamFlag);
// Helper: PHP serializa array associativo VAZIO como `[]`, não como `{}`
// (json_encode([]) === '[]'). Painéis XUI.one/PHP mandam
// `{"info":[],"movie_data":[]}` quando o id não existe mais no catálogo —
// e `z.object().optional()` REJEITA array, derrubando o `.parse()` da ficha
// INTEIRA. Isso é a MESMA classe do crash da "Lost", uma camada acima: o fix
// anterior tolerou os CAMPOS (`null` em metadado) mas deixou o SHAPE rígido.
//
// Trata apenas array VAZIO como "ausente" — array com conteúdo continua
// falhando, porque aí não é o artefato do PHP, é resposta que não entendemos
// e queremos ver falhar. Tolerância no CAMPO, nunca no item
// (project_provider_data_quality).
const emptyPhpArrayAsUndefined = (v) => Array.isArray(v) && v.length === 0 ? undefined : v;
/** Idem, no nível do objeto raiz: `[]` vira `{}` pra o parse seguir e a
 *  DECISÃO ("isto sumiu do catálogo") ficar com quem tem contexto de
 *  produto, não com o validador de schema. */
const emptyPhpArrayAsObject = (v) => Array.isArray(v) && v.length === 0 ? {} : v;
// Helper: número opcional que pode vir como string vazia
const flexNumber = z
    .union([z.string(), z.number(), z.null()])
    .optional()
    .transform((v) => {
    if (v === null || v === undefined || v === '')
        return undefined;
    const n = typeof v === 'number' ? v : Number(v);
    return isNaN(n) ? undefined : n;
});
// ===== Auth =====
export const XtreamAuthSchema = z.object({
    user_info: z.object({
        username: z.string(),
        password: z.string(),
        auth: flexBool.optional(),
        status: z.string().optional(),
        exp_date: flexString.nullable().optional(),
        is_trial: flexString.optional(),
        active_cons: flexString.optional(),
        max_connections: flexString.optional(),
    }),
    server_info: z.object({
        url: z.string().optional(),
        port: flexString.optional(),
        https_port: flexString.optional(),
        server_protocol: z.string().optional(),
        rtmp_port: flexString.optional(),
        timezone: z.string().optional(),
        timestamp_now: flexNumber,
        time_now: z.string().optional(),
    }),
});
// ===== Live categories =====
export const XtreamLiveCategorySchema = z.object({
    category_id: flexString,
    category_name: z.string(),
    parent_id: flexNumber.optional(),
});
// ===== Live streams =====
export const XtreamLiveStreamSchema = z.object({
    num: flexNumber,
    name: z.string(),
    // LISTING (drop-por-item): um stream_type null derrubaria SILENCIOSAMENTE o
    // canal inteiro do catálogo. É metadado não-essencial (name/stream_id bastam) →
    // nullableString pra o canal sobreviver com stream_type null.
    stream_type: nullableString,
    stream_id: flexString,
    stream_icon: z.string().nullable().optional(),
    epg_channel_id: z.string().nullable().optional(),
    // LISTING: `added` é hint de recência (não identidade/placement), lido com guard
    // `if (s.added)`. Null aqui derrubaria o canal do catálogo → nullableFlex.
    added: flexString.nullable().optional(),
    category_id: flexString,
    custom_sid: z.string().nullable().optional(),
    tv_archive: flexNumber.optional(),
    direct_source: z.string().nullable().optional(),
    tv_archive_duration: flexNumber.optional(),
    is_adult: z.union([z.string(), z.number(), z.boolean()]).nullable().optional(),
});
// ===== VOD categories =====
export const XtreamVodCategorySchema = XtreamLiveCategorySchema;
// ===== VOD streams =====
export const XtreamVodStreamSchema = z.object({
    num: flexNumber,
    name: z.string(),
    // LISTING (drop-por-item): stream_type null derrubaria o filme inteiro do
    // catálogo silenciosamente. Metadado não-essencial → nullableString.
    stream_type: nullableString,
    stream_id: flexString,
    stream_icon: z.string().nullable().optional(),
    // Chaves de imagem alternativas que alguns painéis Xtream usam em vez de
    // `stream_icon` na listagem de VOD. Declaradas explicitamente (NÃO via
    // .passthrough()) pra evitar custo de payload no snapshot Redis. Sem isso o
    // Zod stripava e o normalize perdia o poster do filme.
    cover: z.string().nullable().optional(),
    cover_big: z.string().nullable().optional(),
    movie_image: z.string().nullable().optional(),
    rating: flexString.nullable().optional(),
    rating_5based: flexNumber.optional(),
    // LISTING: `added` é hint de recência não-essencial; null derrubaria o filme do
    // catálogo → nullableFlex (consumidor null-guarda).
    added: flexString.nullable().optional(),
    category_id: flexString,
    container_extension: z.string().nullable().optional(),
    custom_sid: z.string().nullable().optional(),
    direct_source: z.string().nullable().optional(),
});
// ===== VOD info detalhado =====
// HARD-parse (getVodInfo usa `.parse()`, NÃO drop-por-item como o listing): UM
// campo `null` num metadado derruba a ficha INTEIRA -> backend 400a -> "abrir o
// filme fecha o app" (mesma classe do crash da "Lost", só que pra VOD). Por isso
// TODO metadado de string opcional aqui é `nullableString`/`.nullable()` — o único
// campo que fica estrito é o id essencial (`movie_data.stream_id`), onde null é
// erro real que queremos ver. Providers Xtream sujos mandam esses metadados como
// `null` em vez de omitir (project_provider_data_quality).
export const XtreamVodInfoSchema = z.preprocess(emptyPhpArrayAsObject, z.object({
    info: z.preprocess(emptyPhpArrayAsUndefined, z
        .object({
        kinopoisk_url: z.string().nullable().optional(),
        tmdb_id: flexString.nullable().optional(),
        name: nullableString,
        o_name: nullableString,
        cover_big: z.string().nullable().optional(),
        movie_image: z.string().nullable().optional(),
        releasedate: nullableString,
        release_date: nullableString,
        youtube_trailer: z.string().nullable().optional(),
        director: nullableString,
        actors: nullableString,
        cast: nullableString,
        description: nullableString,
        plot: nullableString,
        age: nullableString,
        mpaa_rating: nullableString,
        rating_count_kinopoisk: flexNumber.optional(),
        country: nullableString,
        genre: nullableString,
        // TOLERANTE: alguns painéis (ex.: HX) mandam backdrop_path como string
        // crua ou null em vez de array de string. O schema rígido
        // `z.array(z.string())` fazia o safeParse do ITEM INTEIRO falhar, e o
        // parseArray descartava o item silenciosamente (23,4% das séries do HX
        // sumiam do snapshot). Aceitamos array | string | null. O único consumidor
        // (normalize.ts) já tem guard `Array.isArray()` — formatos não-array
        // viram backdrop null sem quebrar.
        backdrop_path: z.union([z.array(z.string()), z.string(), z.null()]).optional(),
        duration_secs: flexNumber.optional(),
        duration: nullableString,
        bitrate: flexNumber.optional(),
        rating: flexString.nullable().optional(),
        // MediaInfo do Xtream — usado pra derivar quality (4K/FHD/HD/SD)
        // quando o nome não tem tag explícita. Nem todos parceiros enviam.
        // MediaInfo parcial do painel manda os campos de codec/bitrate como
        // `null` -> `.nullable()` pra não derrubar a ficha inteira (HARD-parse).
        video: z
            .object({
            width: flexNumber.optional(),
            height: flexNumber.optional(),
            codec_name: nullableString,
            codec_long_name: nullableString,
            bit_rate: flexString.nullable().optional(),
        })
            .passthrough()
            .optional(),
        audio: z
            .object({
            codec_name: nullableString,
            codec_long_name: nullableString,
            channels: flexNumber.optional(),
        })
            .passthrough()
            .optional(),
    })
        .optional()),
    movie_data: z.preprocess(emptyPhpArrayAsUndefined, z
        .object({
        // stream_id ESTRITO de propósito: é o id essencial do filme; null aqui é
        // erro real (não metadado sujo) e deve falhar visível, não ser tolerado.
        // A tolerância acima é de SHAPE (bloco inteiro ausente), não de campo:
        // se o bloco veio, o id tem que estar lá e certo.
        stream_id: flexString,
        name: nullableString,
        added: flexString.nullable().optional(),
        category_id: flexString.nullable().optional(),
        container_extension: z.string().nullable().optional(),
        custom_sid: z.string().nullable().optional(),
        direct_source: z.string().nullable().optional(),
    })
        .optional()),
}));
// ===== Series categories =====
export const XtreamSeriesCategorySchema = XtreamLiveCategorySchema;
// ===== Series =====
export const XtreamSeriesSchema = z.object({
    num: flexNumber,
    name: z.string(),
    series_id: flexString,
    cover: z.string().nullable().optional(),
    // Chaves de imagem alternativas que alguns painéis Xtream usam em vez de
    // `cover` na listagem de séries. Declaradas explicitamente (NÃO via
    // .passthrough()) pra evitar custo de payload no snapshot Redis — só as de
    // imagem interessam. Sem isso o Zod stripava e o normalize perdia o poster.
    stream_icon: z.string().nullable().optional(),
    cover_big: z.string().nullable().optional(),
    movie_image: z.string().nullable().optional(),
    // LISTING (drop-por-item): metadado de série null derrubaria a série INTEIRA do
    // catálogo silenciosamente — mesma família do drop de 23% do backdrop_path. São
    // metadados não-essenciais (series_id/name/category_id bastam) → nullableString
    // pra a série sobreviver com o campo null.
    plot: nullableString,
    cast: nullableString,
    director: nullableString,
    genre: nullableString,
    releaseDate: nullableString,
    release_date: nullableString,
    // LISTING: `last_modified` é hint de frescor (lido com `if (s.last_modified)`),
    // não identidade → nullableFlex pra a série não sumir do catálogo por null.
    last_modified: flexString.nullable().optional(),
    rating: flexString.nullable().optional(),
    rating_5based: flexNumber.optional(),
    // TOLERANTE (mesma motivação da nota em XtreamVodInfoSchema.info): HX manda
    // backdrop_path da SÉRIE como string/null em vez de array → o safeParse do
    // item inteiro falhava e o parseArray descartava a série toda (poster sumia
    // na curadoria). Aceitamos array | string | null.
    backdrop_path: z.union([z.array(z.string()), z.string(), z.null()]).optional(),
    youtube_trailer: z.string().nullable().optional(),
    // LISTING: metadado não-essencial; null derrubaria a série → nullableFlex.
    episode_run_time: flexString.nullable().optional(),
    category_id: flexString,
});
// ===== Series info (temporadas + episódios) =====
export const XtreamEpisodeSchema = z.object({
    // id ESTRITO: identificador essencial do episódio; null é erro real (não
    // metadado sujo) e deve falhar visível.
    id: flexString,
    // episode_num/added: `.nullable()` — mesma classe do crash da "Lost". O fix
    // original endureceu só title/releasedate/plot/duration; um provedor sujo que
    // mande `episode_num`/`added` = null derrubaria a ficha INTEIRA do mesmo jeito
    // (HARD-parse). Fechamos o resto da classe no episódio.
    episode_num: flexString.nullable().optional(),
    // title/releasedate/plot/duration: nullableString — provedores sujos mandam null
    // nesses campos de metadado de episódio e derrubavam o `.parse()` da ficha inteira
    // (ver comentário do `nullableString`). Bug do "Lost" (softxd/HXTV).
    title: nullableString,
    container_extension: z.string().nullable().optional(),
    info: z
        .object({
        tmdb_id: flexString.nullable().optional(),
        releasedate: nullableString,
        plot: nullableString,
        duration_secs: flexNumber.optional(),
        duration: nullableString,
        movie_image: z.string().nullable().optional(),
        cover_big: z.string().nullable().optional(),
        bitrate: flexNumber.optional(),
        rating: flexString.nullable().optional(),
        season: flexNumber.optional(),
    })
        .optional(),
    added: flexString.nullable().optional(),
    season: flexNumber.optional(),
    custom_sid: z.string().nullable().optional(),
    direct_source: z.string().nullable().optional(),
});
export const XtreamSeriesInfoSchema = z.preprocess(emptyPhpArrayAsObject, z.object({
    seasons: z.array(z.unknown()).optional(),
    // NÍVEL SÉRIE do HARD-parse: o fix original da "Lost" endureceu só os campos de
    // EPISÓDIO (XtreamEpisodeSchema), mas este bloco `info` da SÉRIE continuava
    // `z.string().optional()` — um provedor sujo com `info.plot`/`cast`/`genre` = null
    // no topo da ficha derrubaria a série INTEIRA do mesmo jeito. Fechamos a classe
    // aqui: todo metadado de string vira `nullableString`/`.nullable()`.
    info: z.preprocess(emptyPhpArrayAsUndefined, z
        .object({
        name: nullableString,
        cover: z.string().nullable().optional(),
        plot: nullableString,
        cast: nullableString,
        director: nullableString,
        genre: nullableString,
        releaseDate: nullableString,
        release_date: nullableString,
        last_modified: flexString.nullable().optional(),
        rating: flexString.nullable().optional(),
        rating_5based: flexNumber.optional(),
        // TOLERANTE (espelha XtreamSeriesSchema): backdrop_path da ficha de série
        // pode vir string/null em painéis como o HX. Aceitamos array | string |
        // null pra não derrubar o parse da ficha inteira.
        backdrop_path: z.union([z.array(z.string()), z.string(), z.null()]).optional(),
        youtube_trailer: z.string().nullable().optional(),
        episode_run_time: flexString.nullable().optional(),
        category_id: flexString.nullable().optional(),
        // Classificação etária — nem todo provedor envia
        age: nullableString,
        mpaa_rating: nullableString,
    })
        .optional()),
    // `episodes: []` (série sem episódio no painel, ou id que sumiu) é o mesmo
    // artefato do PHP: `z.record` rejeita array e derrubaria a ficha inteira.
    episodes: z.preprocess(emptyPhpArrayAsUndefined, z.record(z.string(), z.array(XtreamEpisodeSchema)).optional()),
}));
// ===== Short EPG (now + next) =====
export const XtreamShortEpgItemSchema = z.object({
    id: flexString.optional(),
    epg_id: flexString.optional(),
    title: z.string(),
    // LISTING (drop-por-item): metadado de display do EPG null derrubaria o
    // programa inteiro (perde o slot now/next). description/lang vêm null com
    // frequência de painéis reais → nullableString. Consumidor lê `?? ''`.
    lang: nullableString,
    start: nullableString,
    end: nullableString,
    description: nullableString,
    channel_id: flexString.optional(),
    start_timestamp: flexString.optional(),
    stop_timestamp: flexString.optional(),
    has_archive: flexNumber.optional(),
    now_playing: flexNumber.optional(),
});
//# sourceMappingURL=xtream-types.js.map